/**
 * Mirror Guardian — co-located API client (P5 Wave 4 §2).
 *
 * Calls the `buildMirrorGuardianRouter(pool)` endpoints:
 *   GET  /  → { success, config }
 *   PUT  /  → { success, config }   (validate: floors ∈ [0,1],
 *              evidence_floor_decline ≤ evidence_band_hedge,
 *              max_history_turns ∈ 1..50; invalid → 400 GUARDIAN_BOUNDS)
 *
 * Per the P5 Wave 4 contract, page API calls live in a co-located `api.ts`
 * using the same fetch/unwrap seam as `services/quillAdminService.ts`. That
 * module does NOT export its `request`/`unwrap` helpers (only `QuillApiError`),
 * so the tiny helper is replicated here — identical behaviour: same-origin
 * fetch through the dev-server proxy (which injects the internal API key
 * server-side), `{success:false}`-is-an-error semantics, and `code` parsing
 * off the JSON body so the page can branch on `GUARDIAN_BOUNDS`.
 *
 * BASE_SEG is the router mount segment. The orchestrator (server.js) wires the
 * mount; this MUST match it. Kept as a single constant so a mount-name change
 * is a one-line reconcile at merge.
 */

import { QuillApiError } from '../../../services/quillAdminService';

const BASE = '/api/admin/quill';
const BASE_SEG = '/mirror-guardian';

interface EnvelopeShape {
  success?: boolean;
  code?: string;
  message?: string;
  error?: string;
  [key: string]: unknown;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    throw new QuillApiError(
      e instanceof Error ? e.message : 'Network error contacting Quill backend',
      0,
      'NETWORK_ERROR'
    );
  }

  let body: EnvelopeShape | null = null;
  try {
    body = await res.json();
  } catch {
    // No/invalid JSON body — handled below.
  }

  if (!res.ok || (body && body.success === false)) {
    const message = body?.message || body?.error || `Quill backend request failed (${res.status})`;
    throw new QuillApiError(message, res.status, body?.code);
  }

  return (body ?? {}) as T;
}

// ── Types ───────────────────────────────────────────────────────────────

export interface GuardianConfig {
  /** Evidence score below which the Mirror declines to answer. 0..1. */
  evidence_floor_decline: number;
  /** Evidence score below which the Mirror hedges. 0..1. Must be ≥ decline. */
  evidence_band_hedge: number;
  /** Likeness score below which a Mirror reply is revised. 0..1. */
  likeness_revision_floor: number;
  /** How many prior turns of context the Mirror considers. Integer 1..50. */
  max_history_turns: number;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface UpdateGuardianConfigRequest {
  evidence_floor_decline: number;
  evidence_band_hedge: number;
  likeness_revision_floor: number;
  max_history_turns: number;
  reason: string;
}

// ── Client ──────────────────────────────────────────────────────────────

export const mirrorGuardianApi = {
  /** GET / — current active guardian config. */
  async getConfig(): Promise<GuardianConfig> {
    const body = await request<{ config?: GuardianConfig } & GuardianConfig>(`${BASE_SEG}/`);
    // Endpoint replies `{ success, config }`; fall back to a flat body if the
    // envelope is ever inlined.
    return (body.config ?? (body as GuardianConfig));
  },

  /** PUT / — write new config. Backend re-validates; 400 GUARDIAN_BOUNDS on
   *  bad bounds (surfaced to the page via QuillApiError.code). */
  async updateConfig(payload: UpdateGuardianConfigRequest): Promise<GuardianConfig> {
    const body = await request<{ config?: GuardianConfig } & GuardianConfig>(`${BASE_SEG}/`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return (body.config ?? (body as GuardianConfig));
  },
};

export default mirrorGuardianApi;
